<template>
  <section class="deck-shuffle" aria-labelledby="deck-shuffle-title">
    <h1 id="deck-shuffle-title" class="sr-only">
      Reclaim your thoughts card deck
    </h1>

    <div
      class="deck-shuffle__stage"
      :class="{ 'is-shuffling': isShuffling, 'has-drawn': hasDrawn }"
    >
      <img
        v-if="!hasDrawn"
        class="deck-shuffle__opening"
        src="/deck-shuffle/opening-cards.png"
        alt="Three Reclaim Your Thoughts cards fanned out"
      >

      <div v-else class="deck-shuffle__drawn" aria-live="polite">
        <img
          class="deck-shuffle__card deck-shuffle__card--left"
          :src="leftCard"
          alt=""
          aria-hidden="true"
        >
        <img
          class="deck-shuffle__card deck-shuffle__card--active"
          :src="activeCard"
          alt="Your mindset card"
        >
        <img
          class="deck-shuffle__card deck-shuffle__card--right"
          :src="rightCard"
          alt=""
          aria-hidden="true"
        >
      </div>
    </div>

    <div class="deck-shuffle__actions">
      <button
        class="deck-shuffle__button"
        type="button"
        :disabled="isShuffling"
        @click="drawCard"
      >
        {{ buttonLabel }}
      </button>

      <a
        class="deck-shuffle__button deck-shuffle__button--download"
        href="/little-lifts.zip"
        download="little-lifts.zip"
      >
        Download them all
      </a>
    </div>
  </section>
</template>

<script setup lang="ts">
const cards = [
  '/deck-shuffle/Print_OYM-SDOA26-19-02_Move001.png',
  '/deck-shuffle/Print_OYM-SDOA26-19-03_Move002.png',
  '/deck-shuffle/Print_OYM-SDOA26-19-04_Move003.png',
  '/deck-shuffle/Print_OYM-SDOA26-19-05_Share001.png',
  '/deck-shuffle/Print_OYM-SDOA26-19-06_Share002.png',
  '/deck-shuffle/Print_OYM-SDOA26-19-07_Share003.png',
  '/deck-shuffle/Print_OYM-SDOA26-19-08_Think001.png',
  '/deck-shuffle/Print_OYM-SDOA26-19-09_Think002.png',
  '/deck-shuffle/Print_OYM-SDOA26-19-10_Think003.png'
];

const activeIndex = ref(0);
const leftIndex = ref(3);
const rightIndex = ref(6);
const hasDrawn = ref(false);
const isShuffling = ref(false);
let shuffleTimer: ReturnType<typeof setTimeout> | undefined;

const activeCard = computed(() => cards[activeIndex.value]);
const leftCard = computed(() => cards[leftIndex.value]);
const rightCard = computed(() => cards[rightIndex.value]);
const buttonLabel = computed(() => {
  if (isShuffling.value) return 'Shuffling…';
  return hasDrawn.value ? 'Try another Little Lift' : 'Try a Little Lift now';
});

function shuffledIndexes() {
  return cards.map((_, index) => index).sort(() => Math.random() - 0.5);
}

function drawCard() {
  if (isShuffling.value) return;
  const wasDrawn = hasDrawn.value;
  let nextIndexes = shuffledIndexes();

  while (
    (wasDrawn &&
      [activeIndex.value, leftIndex.value, rightIndex.value].includes(
        nextIndexes[0]
      )) ||
    (!wasDrawn && [leftIndex.value, rightIndex.value].includes(nextIndexes[0]))
  ) {
    nextIndexes = shuffledIndexes();
  }

  activeIndex.value = nextIndexes[0];

  hasDrawn.value = true;
  isShuffling.value = true;

  shuffleTimer = setTimeout(() => {
    isShuffling.value = false;
  }, 600);
}

onBeforeUnmount(() => {
  if (shuffleTimer) clearTimeout(shuffleTimer);
});
</script>

<style scoped lang="scss">
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.deck-shuffle {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: clamp(1rem, 2.5vh, 1.75rem);
  width: 100%;
  min-height: 100svh;
  padding: 1.5rem 1.25rem;
  background: #b8d96d;

  &__stage {
    display: grid;
    width: min(74vw, 610px);
    height: min(53vw, 445px);
    max-height: calc(100svh - 150px);
    place-items: center;
  }

  &__opening {
    width: 100%;
    max-width: 610px;
    transform-origin: center;
  }

  &__drawn {
    position: relative;
    display: grid;
    width: min(50vw, 330px);
    height: 90%;
    place-items: center;
  }

  &__card {
    position: absolute;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    filter: drop-shadow(0 12px 8px rgba(34, 42, 18, 25%));

    &--active {
      z-index: 2;
      height: 100%;
    }

    &--left,
    &--right {
      height: 100%;
      opacity: 0.82;
    }

    &--left {
      z-index: 1;
      transform: translate(-14%, 6%) rotate(-13deg);
    }

    &--right {
      z-index: 0;
      transform: translate(14%, 6%) rotate(13deg);
    }
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-top: 40px;
  }

  &__button {
    min-width: min(320px, 76vw);
    padding: 0.85rem 2rem;
    border-radius: 12px;
    background: #b266b6;
    color: #fff;
    font-size: clamp(1rem, 2vw, 1.35rem);
    font-weight: 700;
    line-height: 1.2;
    text-align: center;
    text-decoration: none;
    transition:
      background 180ms ease,
      transform 180ms ease;

    &:hover {
      background: #9d54a2;
      transform: translateY(-2px);
    }

    &:focus-visible {
      outline: 4px solid #fff;
      outline-offset: 4px;
    }

    &:disabled {
      cursor: wait;
      opacity: 0.8;
    }

    &--download {
      background: #13877e;

      &:hover {
        background: #0e746c;
      }
    }
  }
}

.is-shuffling {
  .deck-shuffle__card--active {
    animation: shuffle 600ms cubic-bezier(0.22, 1, 0.36, 1);
  }
}

@keyframes shuffle {
  0%,
  100% {
    transform: translateX(0) rotate(0);
  }

  30% {
    transform: translateX(-6%) rotate(-3deg);
  }

  65% {
    transform: translateX(6%) rotate(3deg);
  }
}

@media (width <= 640px) {
  .deck-shuffle {
    gap: 1rem;

    &__stage {
      width: min(94vw, 475px);
      height: min(70vw, 350px);
    }

    &__button {
      min-width: min(290px, 78vw);
      border-radius: 10px;
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .is-shuffling .deck-shuffle__card--active {
    animation: none;
  }
}
</style>
